/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    PaginationModel
} from "@insite/client-framework/Types/ApiModels";
import CustomerCard, { CustomerCardStyles } from "@insite/content-library/Components/CustomerCard";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    customers: BaseAddressModel[];
    pagination: PaginationModel | null;
    selectedCustomer?: BaseAddressModel;
    onSelect: (customer: BaseAddressModel) => void;
    /** @deprecated Not used anymore */
    allowEditCustomer?: boolean;
    onEdit?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => void;
    onChangePage: (page: number) => void;
    onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    extendedStyles?: CustomerSelectorStyles;
    isOverlay?: boolean;

    // customization
    editText?: string;
    addressFields?: AddressFieldDisplayCollectionModel;
}

export interface CustomerSelectorStyles {
    customerCard?: CustomerCardStyles;
    customerCardSelected?: CustomerCardStyles;
    pagination?: PaginationPresentationProps;
}

const linkTextStyles: TypographyPresentationProps = {
    color: "text.link",
};

export const customerSelectorStyles: CustomerSelectorStyles = {
    customerCardSelected: {
        address: {
            attentionText: linkTextStyles,
            customerNameText: linkTextStyles,
            companyNameText: linkTextStyles,
            address1Text: linkTextStyles,
            address2Text: linkTextStyles,
            address3Text: linkTextStyles,
            address4Text: linkTextStyles,
            cityStatePostalCodeText: linkTextStyles,
            countryText: linkTextStyles,
            phoneText: linkTextStyles,
            emailText: linkTextStyles,
            faxText: linkTextStyles,
        },
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                margin-top: 20px;

                @media only screen and (max-width: 415px) {
                    display: flex;
                    flex-direction: row;
                    > nav {
                        magin-top: 12px;
                        justify-content: flex-start;
                    }
                }
            `,
        },
    },
};

const CustomerSelector = ({
    customers,
    pagination,
    allowEditCustomer = false,
    extendedStyles,
    selectedCustomer,
    isOverlay,
    onChangePage,
    onChangeResultsPerPage,
    onSelect,
    onEdit,
    editText,
    addressFields,
}: OwnProps) => {
    const [styles] = React.useState(() => mergeToNew(customerSelectorStyles, extendedStyles));

    return (
        <>
            {customers.map(customer => {
                const isCardSelected = customer.id === selectedCustomer?.id;
                return (
                    <CustomerCard
                        key={customer.id.toString()}
                        customer={customer}
                        isSelected={isCardSelected}
                        extendedStyles={isCardSelected ? styles.customerCardSelected : styles.customerCard}
                        onSelect={onSelect}
                        onEdit={onEdit}
                        isOverlay={isOverlay}
                        {...({ editText } as any)}
                        {...({ addressFields } as any)}
                    />
                );
            })}
            {customers.length > 0 && pagination && (
                <Pagination
                    {...styles.pagination}
                    currentPage={pagination.currentPage}
                    resultsPerPage={pagination.pageSize}
                    resultsCount={pagination.totalItemCount}
                    resultsPerPageOptions={pagination.pageSizeOptions}
                    onChangePage={onChangePage}
                    resultsPerPageLabel="Page Size"
                    onChangeResultsPerPage={onChangeResultsPerPage}
                    prevPage={pagination.prevPageUri}
                    nextPage={pagination.nextPageUri}
                />
            )}
        </>
    );
};

export default CustomerSelector;
